/**
 * Animated Stat Card Component
 * Displays statistics with animated number counters
 *
 * @author SAUBHAGYA Development Team
 * @since Epic 6
 */

import { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  /**
   * Title of the stat
   */
  title: string;

  /**
   * Numeric value to display (will be animated)
   */
  value: number;

  /**
   * Optional prefix (e.g., currency symbol)
   */
  prefix?: string;

  /**
   * Optional suffix (e.g., "KG", "%")
   */
  suffix?: string;

  /**
   * Number of decimal places
   * @default 0
   */
  decimals?: number;

  /**
   * Icon to display
   */
  icon?: LucideIcon;

  /**
   * Color variant
   * @default 'default'
   */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';

  /**
   * Description or subtext
   */
  description?: string;

  /**
   * Trend indicator (+/- percentage)
   */
  trend?: {
    value: number;
    label?: string;
  };

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Animation duration in ms
   * @default 1000
   */
  animationDuration?: number;
}

/**
 * Animated counter hook
 */
function useAnimatedCounter(
  endValue: number,
  duration: number = 1000,
  decimals: number = 0
): number {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const startValue = 0;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);

      // Easing function (ease-out-cubic)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);

      const currentValue = startValue + (endValue - startValue) * easeOutCubic;
      setCount(Number(currentValue.toFixed(decimals)));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [endValue, duration, decimals]);

  return count;
}

/**
 * Format number with commas
 */
function formatNumber(value: number, decimals: number = 0): string {
  return value.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Variant configurations
 */
const variantConfig = {
  default: {
    iconBg: 'bg-slate-100 dark:bg-slate-800',
    iconColor: 'text-slate-600 dark:text-slate-400',
  },
  success: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  warning: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  danger: {
    iconBg: 'bg-red-100 dark:bg-red-900/50',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  info: {
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
};

export function StatCard({
  title,
  value,
  prefix,
  suffix,
  decimals = 0,
  icon: Icon,
  variant = 'default',
  description,
  trend,
  className,
  animationDuration = 1000,
}: StatCardProps) {
  const animatedValue = useAnimatedCounter(value, animationDuration, decimals);
  const config = variantConfig[variant];

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all duration-300',
        'hover:shadow-lg hover:scale-[1.02]',
        'border border-border/50',
        className
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-1">
              {prefix && (
                <span className="text-lg font-medium text-muted-foreground">
                  {prefix}
                </span>
              )}
              <span className="text-3xl font-bold tracking-tight">
                {formatNumber(animatedValue, decimals)}
              </span>
              {suffix && (
                <span className="text-lg font-medium text-muted-foreground">
                  {suffix}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
            {trend && (
              <div className="flex items-center gap-1 pt-1">
                <span
                  className={cn(
                    'text-xs font-medium',
                    trend.value >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400'
                  )}
                >
                  {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}%
                </span>
                {trend.label && (
                  <span className="text-xs text-muted-foreground">
                    {trend.label}
                  </span>
                )}
              </div>
            )}
          </div>

          {Icon && (
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                config.iconBg
              )}
            >
              <Icon className={cn('h-6 w-6', config.iconColor)} />
            </div>
          )}
        </div>
      </CardContent>

      {/* Decorative gradient */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 h-1',
          variant === 'success' && 'bg-gradient-to-r from-emerald-400 to-emerald-600',
          variant === 'warning' && 'bg-gradient-to-r from-amber-400 to-amber-600',
          variant === 'danger' && 'bg-gradient-to-r from-red-400 to-red-600',
          variant === 'info' && 'bg-gradient-to-r from-blue-400 to-blue-600',
          variant === 'default' && 'bg-gradient-to-r from-slate-300 to-slate-500'
        )}
      />
    </Card>
  );
}

export default StatCard;
